import type { IChannelControls, ITelemetryPlugin } from '@microsoft/applicationinsights-core-js'
import { ApplicationInsights, IExceptionTelemetry, SeverityLevel } from '@microsoft/applicationinsights-web'
import { ReactPlugin } from '@microsoft/applicationinsights-react-js'
import { OfflineChannel } from '@microsoft/applicationinsights-offlinechannel-js'
import { createBrowserHistory } from 'history'

let appInsightsInstance: ApplicationInsights | null = null
let reactPluginInstance: ReactPlugin | null = null

const browserHistory = createBrowserHistory()

const getReactPlugin = () => {
    if (!reactPluginInstance) {
        reactPluginInstance = new ReactPlugin()
    }
    return reactPluginInstance
}

export const getAppInsights = () => {
    if (appInsightsInstance) {
        return appInsightsInstance
    }

    const connectionString = import.meta.env.VITE_APP_INSIGHTS_CONNECTION_STRING as string | undefined
    if (!connectionString) {
        return null
    }

    const reactPlugin = getReactPlugin()

    const offlineChannel = new OfflineChannel() as unknown as IChannelControls

    appInsightsInstance = new ApplicationInsights({
        config: {
            connectionString,
            enableAutoRouteTracking: true,
            autoTrackPageVisitTime: true,
            enableCorsCorrelation: true,
            extensions: [reactPlugin as unknown as ITelemetryPlugin],
            extensionConfig: {
                [reactPlugin.identifier]: { history: browserHistory },
            },
            channels: [[offlineChannel]],
        },
    })
    appInsightsInstance.loadAppInsights()

    return appInsightsInstance
}

export const trackException = (
    error: unknown,
    options?: { severityLevel?: SeverityLevel; properties?: IExceptionTelemetry['properties'] }
) => {
    const ai = getAppInsights()
    if (!ai) return

    const { exception, properties } = normaliseError(error, options?.properties)

    ai.trackException({
        exception,
        severityLevel: options?.severityLevel,
        properties,
    })
}

const normaliseError = (
    error: unknown,
    baseProps: IExceptionTelemetry['properties'] = {}
): { exception: Error; properties: IExceptionTelemetry['properties'] } => {
    // If it is already an Error, keep it and just add props if we can
    if (error instanceof Error) {
        return {
            exception: error,
            properties: baseProps,
        }
    }

    // Strings: use as message
    if (typeof error === 'string') {
        return {
            exception: new Error(error),
            properties: baseProps,
        }
    }

    // Objects: try to JSON stringify and also send fields as customDimensions
    if (error && typeof error === 'object') {
        let serialised = '[unserialisable error object]'
        try {
            serialised = JSON.stringify(error)
        } catch {
            // ignore; keep fallback string
        }

        return {
            exception: new Error(serialised),
            properties: {
                ...baseProps,
                // flatten the original object into customDimensions where possible
                errorObject: serialised,
            },
        }
    }

    // Numbers, booleans, null, undefined etc
    return {
        exception: new Error(String(error)),
        properties: baseProps,
    }
}
